import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  useCreateTherapyDiaryEntryMutation,
  useGetTherapyDiaryEntriesQuery,
  useRecallTherapyDiaryEntryMutation,
  useSendTherapyDiaryEntryMutation,
  useUpdateTherapyDiaryEntryMutation,
} from '../../../services/therapy-diary.api';
import type { IJournalResult } from '../../../store/types';

import { buildDiaryPayload, entryToSelection, isEmptyPayload } from './build-diary-payload';
import { useClientAssignmentHydration, useClientAssignmentSentFlag, useClientAssignmentState } from './context';
import { diaryErrorMessage } from './diary-errors';

const hasStatus = (error: unknown): error is { status: unknown } =>
  typeof error === 'object' && error !== null && 'status' in error;

const isConflict = (error: unknown): boolean => hasStatus(error) && error.status === 409;

export interface DiarySubmitResult {
  status: 'idle' | 'loading';
  /** Задание отправлено — повторная отправка закрыта. */
  isSent: boolean;
  isRestoring: boolean;
  onSaveDraft: () => void;
  onSend: () => void;
  /** Вернуть отправленное задание в черновик, чтобы переписать и отправить заново. */
  onRecall: () => void;
}

/** Сохранение и отправка задания клиенту. Вызывать внутри ClientAssignmentProvider. */
export const useDiarySubmit = (consultationId: number, meetData: IJournalResult | null): DiarySubmitResult => {
  const {
    data,
    isLoading: isRestoring,
    refetch: refetchEntries,
  } = useGetTherapyDiaryEntriesQuery({ consultation_id: consultationId }, { skip: !consultationId });
  const selection = useClientAssignmentState();
  const hydrate = useClientAssignmentHydration();
  const setIsSent = useClientAssignmentSentFlag();

  const [createEntry] = useCreateTherapyDiaryEntryMutation();
  const [updateEntry] = useUpdateTherapyDiaryEntryMutation();
  const [sendEntry] = useSendTherapyDiaryEntryMutation();
  const [recallEntry] = useRecallTherapyDiaryEntryMutation();

  const [status, setStatus] = useState<DiarySubmitResult['status']>('idle');

  const entry = useMemo(() => {
    const entries = data?.items ?? [];
    return entries.find((item) => item.status === 'draft') ?? entries.find((item) => item.status === 'sent') ?? null;
  }, [data]);

  // Ref, а не состояние: id нужен внутри обработчиков сразу после создания записи,
  // до того как рефетч списка обновит кэш.
  const entryIdRef = useRef<number | null>(null);
  useEffect(() => {
    entryIdRef.current = entry?.id ?? null;
  }, [entry]);

  useEffect(() => {
    if (!entry) return;

    hydrate(entryToSelection(entry));
  }, [entry, hydrate]);

  const persist = useCallback(async (): Promise<number> => {
    const payload = buildDiaryPayload({ ...selection, meetData });
    const entryId = entryIdRef.current;

    if (entryId !== null) {
      await updateEntry({ id: entryId, consultationId, body: payload }).unwrap();
      return entryId;
    }

    try {
      const created = await createEntry({ consultation_id: consultationId, ...payload }).unwrap();
      entryIdRef.current = created.id;
      return created.id;
    } catch (error: unknown) {
      // 409 значит, что черновик уже есть (другая вкладка, гонка): дочитываем его и пишем в него.
      if (!isConflict(error)) throw error;

      const existing = await refetchEntries().unwrap();
      const draftId = existing.items.find((item) => item.status === 'draft')?.id;
      if (draftId === undefined) throw error;

      entryIdRef.current = draftId;
      await updateEntry({ id: draftId, consultationId, body: payload }).unwrap();
      return draftId;
    }
  }, [consultationId, createEntry, meetData, refetchEntries, selection, updateEntry]);

  const isSent = entry?.status === 'sent';
  const isBusy = status === 'loading' || isSent || !consultationId;

  useEffect(() => {
    setIsSent(isSent);
  }, [isSent, setIsSent]);

  const onSaveDraft = useCallback(() => {
    if (isBusy) return;

    setStatus('loading');
    persist()
      .then(() => toast.success('Черновик сохранён'))
      .catch((error: unknown) => toast.error(diaryErrorMessage(error, 'save')))
      .finally(() => setStatus('idle'));
  }, [isBusy, persist]);

  const onSend = useCallback(() => {
    if (isBusy) return;

    if (isEmptyPayload(buildDiaryPayload({ ...selection, meetData }))) {
      toast.info('Отметьте хотя бы один инструмент или домашнее задание');
      return;
    }

    setStatus('loading');
    // Сохраняем перед отправкой: иначе уйдёт то, что было до последних правок психолога.
    persist()
      .then((entryId) => sendEntry({ id: entryId, consultationId }).unwrap())
      .then(() => toast.success('Задание отправлено клиенту'))
      .catch((error: unknown) => toast.error(diaryErrorMessage(error, 'send')))
      .finally(() => setStatus('idle'));
  }, [consultationId, isBusy, meetData, persist, selection, sendEntry]);

  // Своё условие вместо isBusy: там isSent, а отзыв возможен как раз в отправленном состоянии.
  const onRecall = useCallback(() => {
    const entryId = entryIdRef.current;
    if (status === 'loading' || !isSent || entryId === null) return;

    setStatus('loading');
    recallEntry({ id: entryId, consultationId })
      .unwrap()
      .then(() => toast.success('Задание отозвано — его можно изменить и отправить заново'))
      .catch((error: unknown) => toast.error(diaryErrorMessage(error, 'recall')))
      .finally(() => setStatus('idle'));
  }, [consultationId, isSent, recallEntry, status]);

  return { status, isSent, isRestoring, onSaveDraft, onSend, onRecall };
};
